<template>
  <div>
    <b-loading
        v-model="isLoading"
        :can-cancel="true"
        :is-full-page="true"
    ></b-loading>
    <div class="main-style-menu" style="background: #e9e9e9">
      <transition name="fade" mode="out-in" v-if="selectedArchiveProject === null">
        <search-box-archive ref="searchBoxQuery"
                              @onToggleSelectedProject="onToggleSelectedProject" @onClickedArea="onClickedProjectSearch"></search-box-archive>
      </transition>
      <div v-if="selectedArchiveProject" class="pb-6">
        <div>
          <div class="is-flex has-background-white title-box p-2">
            <div class="is-size-4 ml-5 mr-2 pt-1 pb-1 has-text-weight-bold">{{ selectedArchiveProject.name }}
            </div>
            <b-button type="is-light" icon-left="refresh" class="ml-auto mt-auto mb-auto" @click="refreshData"></b-button>
            <archive-rules-control :rule="projectRule" :can-run="!!appliedProjectRuleGetter(selectedArchiveProject._id)" label="Project Rules" class="mt-auto mb-auto ml-2" @onRuleClicked="onRuleClicked('project')" @onActionClicked="onRunProjectRules"></archive-rules-control>
            <b-button size="is-small" type="is-info" icon-left="restore" class="mt-auto mb-auto ml-2"
                      :disabled="!projectData || !projectData.statusCounts || !projectData.statusCounts.archived"
                      @click="onRestoreProject">
              Restore
            </b-button>
            <b-button size="is-small" type="is-warning" icon-left="content-copy" class="mt-auto mb-auto ml-2"
                      @click="onCloneClicked('project')">
              Clone
            </b-button>
            <b-tooltip class="mt-auto mb-auto mr-2" label="Close project" :delay="1000" type="is-dark"
                       position="is-top">
              <b-button class="card-header-icon" aria-label="more options"
                        @click="closeProjectOverview">
                <b-icon icon="close"></b-icon>
              </b-button>
            </b-tooltip>
          </div>
          <!-- Status count cards -->
          <div v-if="projectData && projectData.statusCounts" class="is-flex is-flex-wrap-wrap p-4 pb-0" style="gap: 12px;">
            <div v-for="(value, key) in projectData.statusCounts" :key="key"
                 class="status-card has-background-white pt-2 pb-2 pl-3 pr-3"
                 :class="getStatusCardClass(key)">
              <div class="is-size-7 has-text-grey">{{ capitalizeFirst(key) }}</div>
              <div class="is-size-5 has-text-weight-bold">{{ value }}</div>
            </div>
          </div>

          <!-- Site collapse cards -->
          <div class="is-flex is-flex-direction-column p-4" style="padding-bottom: 200px !important;">
            <b-collapse
                v-for="site in (projectData ? projectData.siteData : [])"
                :key="site.siteId"
                class="card shadow mb-2"
                animation="slide"
                style="border-radius: 8px; background-color: #f9f9f9;"
                :aria-id="'site-' + site.siteId">
              <template #trigger="props">
                <div
                    class="card-header shadow"
                    role="button"
                    :aria-controls="'site-' + site.siteId"
                    style="border-radius: 8px; background-color: white; min-height: 64px;">
                  <div class="card-header-title pl-5" style="align-items: center; padding: 0;">
                    <div class="columns" style="width: 100%">
                      <div class="column is-4">
                        <div class="is-size-7 has-text-grey-light has-text-weight-normal">Site name</div>
                        <div class="has-text-black">{{ site.siteName }}</div>
                      </div>
                      <div class="column is-1">
                        <div class="is-size-7 has-text-grey-light has-text-weight-normal">Devices</div>
                        <div class="has-text-black">{{ site.devices ? site.devices.length : 0 }}</div>
                      </div>
                      <div class="column is-1">
                        <div class="is-size-7 has-text-grey-light has-text-weight-normal">Total</div>
                        <div class="has-text-black">{{ site.total || 0 }}</div>
                      </div>
                      <div class="column is-5 mr-0 pr-0">
                        <div class="is-size-7 has-text-grey-light has-text-weight-normal">Status</div>
                        <div class="is-flex is-flex-direction-row is-align-items-center">
                          <div class="count-indicator has-text-white is-size-7 has-background-success">Active ({{ site.counts ? site.counts.active : 0 }})</div>
                          <div class="count-indicator has-text-white ml-2 has-background-info is-size-7">Archived ({{ site.counts ? site.counts.archived : 0 }})</div>
                          <div class="count-indicator has-text-white ml-2 has-background-warning is-size-7">Restoring ({{ site.counts ? site.counts.restoring : 0 }})</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <archive-rules-control :rule="findRule('site', site.siteId)" :can-run="!!appliedSiteRuleGetter(site.siteId)" label="Site Rules" class="mt-auto mb-auto mr-2" @onRuleClicked="onRuleClicked('site', site)" @onActionClicked="onRunSiteRules(site)"></archive-rules-control>
                  <b-button size="is-small" type="is-info" icon-left="restore" class="mt-auto mb-auto mr-2"
                            :disabled="!site.counts || !site.counts.archived"
                            @click.stop="onRestoreSite(site)">
                    Restore
                  </b-button>
                  <b-button size="is-small" type="is-warning" icon-left="content-copy" class="mt-auto mb-auto mr-2"
                            @click.stop="onCloneClicked('site', site)">
                    Clone
                  </b-button>
                  <a class="card-header-icon">
                    <b-icon :icon="props.open ? 'chevron-down' : 'chevron-up'"></b-icon>
                  </a>
                </div>
              </template>

              <div class="card-content">
                <table class="table is-fullwidth is-hoverable">
                  <thead>
                    <tr>
                      <th>Device Name</th>
                      <th class="has-text-centered">Total</th>
                      <th class="has-text-centered">Active</th>
                      <th class="has-text-centered">Archived</th>
                      <th class="has-text-centered">Restoring</th>
                      <th class="has-text-centered">Raw Total</th>
                      <th class="has-text-centered">Raw Active</th>
                      <th class="has-text-centered">Raw Deleted</th>
                      <th class="has-text-centered">Raw Restoring</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="device in site.devices" :key="device.dataDeviceId">
                      <td>{{ device.name }}</td>
                      <td class="has-text-centered">{{ device.total }}</td>
                      <td class="has-text-centered">{{ device.counts ? device.counts.active : 0 }}</td>
                      <td class="has-text-centered">{{ device.counts ? device.counts.archived : 0 }}</td>
                      <td class="has-text-centered">{{ device.counts ? device.counts.restoring : 0 }}</td>
                      <td class="has-text-centered">{{ device.rawTotal }}</td>
                      <td class="has-text-centered">{{ device.rawCounts ? device.rawCounts.active : 0 }}</td>
                      <td class="has-text-centered">{{ device.rawCounts ? device.rawCounts.deleted : 0 }}</td>
                      <td class="has-text-centered">{{ device.rawCounts ? device.rawCounts.restoring : 0 }}</td>
                      <td>
                        <div class="is-flex is-justify-content-flex-end is-align-items-center" style="gap: 8px;">
                          <archive-rules-control :rule="findRule('device', device.dataDeviceId)" :can-run="!!appliedDeviceRuleGetter(device.dataDeviceId)" label="Device Rules" @onRuleClicked="onRuleClicked('device', device, site)" @onActionClicked="onRunDeviceRules(device, site)"></archive-rules-control>
                          <b-button size="is-small" type="is-info" icon-left="restore"
                                    :disabled="!device.counts || !device.counts.archived"
                                    @click="onRestoreDevice(device)">
                            Restore
                          </b-button>
                          <b-button size="is-small" type="is-warning" icon-left="content-copy"
                                    @click="onCloneClicked('device', device)">
                            Clone
                          </b-button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>
      <div v-else @click="onClickedOutside" style="height: 80vh"></div>
    </div>
    <!-- Restore confirm dialog -->
    <b-modal :active.sync="restoreConfirmActive" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="max-width: 420px; margin: auto;">
        <header class="modal-card-head">
          <p class="modal-card-title has-text-weight-bold">Restore</p>
          <button class="delete" aria-label="close" @click="restoreConfirmActive = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="is-flex is-justify-content-space-between mb-2">
            <span class="is-size-7 has-text-grey">Level</span>
            <span class="is-size-7 has-text-weight-bold">{{ restoreConfirmLevel }}</span>
          </div>
          <div class="is-flex is-justify-content-space-between mb-2">
            <span class="is-size-7 has-text-grey">{{ restoreConfirmLevel === 'project' ? 'Project' : restoreConfirmLevel === 'site' ? 'Site' : 'Device' }}</span>
            <span class="is-size-7 has-text-weight-bold">{{ restoreConfirmEntityName }}</span>
          </div>
          <div class="notification is-warning is-light is-size-7 mt-3 mb-0">
            Are you sure you want to restore all archived items for this {{ restoreConfirmLevel }}?
          </div>
        </section>
        <footer class="modal-card-foot is-flex is-justify-content-flex-end" style="gap: 8px;">
          <b-button @click="restoreConfirmActive = false">Cancel</b-button>
          <b-button type="is-info" icon-left="restore" @click="executeRestore">
            Restore
          </b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Run confirm dialog -->
    <b-modal :active.sync="runConfirmActive" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="max-width: 460px; margin: auto;">
        <header class="modal-card-head">
          <p class="modal-card-title has-text-weight-bold">Run Archiving</p>
          <button class="delete" aria-label="close" @click="runConfirmActive = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="mb-3">
            <span class="is-size-7 has-text-grey">{{ runConfirmLevel === 'project' ? 'Project' : runConfirmLevel === 'site' ? 'Site' : 'Device' }}:</span>
            <span class="is-size-7 has-text-weight-bold ml-1">{{ runConfirmEntityName }}</span>
          </div>
          <div v-if="runConfirmAppliedRule" class="applied-rule-box">
            <div class="is-size-7 has-text-grey mb-2" style="text-transform: uppercase;">Applied Rule</div>
            <div class="is-flex is-justify-content-space-between mb-1">
              <span class="is-size-7">Level</span>
              <span class="is-size-7 has-text-weight-bold">{{ runConfirmAppliedRule.ruleAppliedAtLevel }}</span>
            </div>
            <div class="is-flex is-justify-content-space-between mb-1">
              <span class="is-size-7">Archive By</span>
              <span class="is-size-7 has-text-weight-bold">{{ runConfirmAppliedRule.archiveBy }}</span>
            </div>
            <div v-if="runConfirmAppliedRule.archiveBy === 'timeSinceLastCaptureDays'" class="is-flex is-justify-content-space-between mb-1">
              <span class="is-size-7">Days Since Last Capture</span>
              <span class="is-size-7 has-text-weight-bold">{{ runConfirmAppliedRule.timeSinceLastCaptureDays }}</span>
            </div>
            <div v-if="runConfirmAppliedRule.archiveBy === 'everyXDays'" class="is-flex is-justify-content-space-between mb-1">
              <span class="is-size-7">Every X Days</span>
              <span class="is-size-7 has-text-weight-bold">{{ runConfirmAppliedRule.everyXDays }}</span>
            </div>
            <div v-if="runConfirmAppliedRule.everyXDaysFirstDayToUpdate" class="is-flex is-justify-content-space-between mb-1">
              <span class="is-size-7">First Day To Update</span>
              <span class="is-size-7 has-text-weight-bold">{{ runConfirmAppliedRule.everyXDaysFirstDayToUpdate }}</span>
            </div>
            <div class="is-flex is-justify-content-space-between mb-1">
              <span class="is-size-7">Status</span>
              <b-tag :type="runConfirmAppliedRule.currentStatus === 'active' ? 'is-success' : 'is-danger'" size="is-small">{{ runConfirmAppliedRule.currentStatus }}</b-tag>
            </div>
            <div v-if="runConfirmAppliedRule.updatedAt" class="is-flex is-justify-content-space-between">
              <span class="is-size-7">Last Updated</span>
              <span class="is-size-7 has-text-weight-bold">{{ runConfirmAppliedRule.updatedAt }}</span>
            </div>
          </div>
          <div v-else class="has-text-centered has-text-grey p-4">
            <b-icon icon="alert-circle-outline" size="is-small"></b-icon>
            <span class="is-size-7 ml-1">No applied rule found</span>
          </div>
        </section>
        <footer class="modal-card-foot is-flex is-justify-content-flex-end" style="gap: 8px;">
          <b-button @click="runConfirmActive = false">Cancel</b-button>
          <b-button type="is-success" icon-left="play" :disabled="!runConfirmAppliedRule" @click="executeRun">
            Run Archiving
          </b-button>
        </footer>
      </div>
    </b-modal>

    <archive-rule-dialog
        :active="ruleDialogActive"
        :level="ruleDialogLevel"
        :entity-name="ruleDialogEntityName"
        :entity-id="ruleDialogEntityId"
        :rule="ruleDialogRule"
        @close="ruleDialogActive = false"
        @onSubmitRule="onSubmitRule"
        @onDeleteRule="onDeleteRule">
    </archive-rule-dialog>

    <clone-dialog
        :active="cloneDialogActive"
        :level="cloneDialogLevel"
        :entity-name="cloneDialogEntityName"
        :entity-id="cloneDialogEntityId"
        :data-type-options="cloningDataTypeOptions"
        :destination-options="currentDestinationOptions"
        @close="cloneDialogActive = false"
        @onClone="onClone">
    </clone-dialog>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import SearchBoxArchive from "@/modules/arhiving-module/components/search-box-archive/search-box-archive.vue";
import ArchiveRulesControl from "@/modules/arhiving-module/components/archive-rules-control/archive-rules-control.vue";
import ArchiveRuleDialog from "@/modules/arhiving-module/components/archive-rule-dialog/archive-rule-dialog.vue";
import CloneDialog from "@/modules/arhiving-module/components/clone-dialog/clone-dialog.vue";

export default {
  name: "archive-page",
  components: {SearchBoxArchive, ArchiveRulesControl, ArchiveRuleDialog, CloneDialog},
  data() {
    return {
      isLoading: false,
      selectedDeviceId: null,
      ruleDialogActive: false,
      ruleDialogLevel: 'project',
      ruleDialogEntityName: '',
      ruleDialogEntityId: '',
      ruleDialogRule: null,
      restoreConfirmActive: false,
      restoreConfirmLevel: 'project',
      restoreConfirmEntityName: '',
      restoreConfirmPayload: null,
      runConfirmActive: false,
      runConfirmLevel: 'project',
      runConfirmEntityName: '',
      runConfirmAppliedRule: null,
      runConfirmPayload: null,
      cloningDataTypeOptions:["raw", "conditioned", "blurred", "snippet"],
      allowedDestinationOptions: {
        production: ["stage", "dev"],
        stage: ["dev"],
        development: ["dev"]
      },
      cloneDialogActive: false,
      cloneDialogLevel: 'project',
      cloneDialogEntityName: '',
      cloneDialogEntityId: '',
    }
  },
  async mounted() {},
  methods: {
    ...mapActions({
      setSelectedArchiveProject: 'archiveModule/setSelectedArchiveProject',
      addNewRule: 'archiveModule/addNewRule',
      updateRule:'archiveModule/updateRule',
      triggerArchiving:'archiveModule/triggerArchiving',
      triggerRestoring:'archiveModule/triggerRestoring',
      triggerCloning:'archiveModule/triggerCloning',
      deleteRule:'archiveModule/deleteRule',
      getProjectRules:'archiveModule/getProjectRules',
      getUsers: 'getUsers',
    }),
    async onToggleSelectedProject(project) {
      console.log('Selected project query - toggle', project)
      if (project === null) {
        await this.setSelectedArchiveProject(null)
      } else {
        this.isLoading = true
        const response = await this.setSelectedArchiveProject(project)
        if (response instanceof Error) {
          this.showToast(`Error: ${response.message}`, 'is-danger')
        }
        this.isLoading = false
      }
    },
    async refreshData(){
      this.isLoading = true
      const response = await this.setSelectedArchiveProject(this.selectedArchiveProject)
      if (response instanceof Error) {
        this.showToast(`Error: ${response.message}`, 'is-danger')
      }
      this.isLoading = false
    },
    closeProjectOverview() {
      this.setSelectedArchiveProject(null)
    },
    onClickedOutside() {
      console.log('On clicked outside')
      if (this.$refs.searchBoxQuery) {
        this.$refs.searchBoxQuery.onClickedOutside()
      }
      if(this.$refs.searchBoxSite){
        this.$refs.searchBoxSite.onClickedOutside()
      }
    },
    onClickedProjectSearch() {
      if(this.$refs.searchBoxSite){
        this.$refs.searchBoxSite.onClickedOutside()
      }
    },
    onMapLoaded() {
      console.log('On map loaded')
    },
    onRuleClicked(level, data, parent) {
      console.log('Rule clicked', level, data, parent)
      if (level === 'project') {
        this.ruleDialogLevel = 'project'
        this.ruleDialogEntityName = this.selectedArchiveProject ? this.selectedArchiveProject.name : ''
        this.ruleDialogEntityId = this.selectedArchiveProject ? this.selectedArchiveProject._id : ''
        this.ruleDialogRule = this.projectRule || null
      } else if (level === 'site') {
        this.ruleDialogLevel = 'site'
        this.ruleDialogEntityName = data ? data.siteName : ''
        this.ruleDialogEntityId = data ? data.siteId : ''
        this.ruleDialogRule = data ? this.findRule('site', data.siteId) : null
      } else if (level === 'device') {
        this.ruleDialogLevel = 'dataDevice'
        this.ruleDialogEntityName = data ? data.name : ''
        this.ruleDialogEntityId = data ? data.dataDeviceId : ''
        this.ruleDialogRule = data ? this.findRule('device', data.dataDeviceId) : null
      }
      this.ruleDialogActive = true
    },
    async onSubmitRule(payload, isEdit) {
      console.log('Submit rule:', payload, 'isEdit:', isEdit)
      this.isLoading = true
      const response = isEdit
          ? await this.updateRule(payload)
          : await this.addNewRule(payload)
      if (response instanceof Error) {
        this.showToast(`Error: ${response.message}`, 'is-danger')
      } else {
        this.showToast(`${response.message}`, 'is-success')
        await this.getProjectRules(this.selectedArchiveProject._id)
        this.ruleDialogActive = false
      }
      this.isLoading = false
    },
    async onDeleteRule(ruleId) {
      console.log('Delete rule:', ruleId)
      this.isLoading = true
      const response = await this.deleteRule(ruleId)
      if (response instanceof Error) {
        this.showToast(`Error: ${response.message}`, 'is-danger')
      } else {
        this.showToast(`${response.message}`, 'is-success')
        await this.getProjectRules(this.selectedArchiveProject._id)
        this.ruleDialogActive = false
      }
      this.isLoading = false
    },
    onRunProjectRules() {
      this.runConfirmLevel = 'project'
      this.runConfirmEntityName = this.selectedArchiveProject ? this.selectedArchiveProject.name : ''
      this.runConfirmAppliedRule = this.appliedProjectRuleGetter(this.selectedArchiveProject._id)
      this.runConfirmPayload = {
        triggerBy: "project",
        id: this.selectedArchiveProject._id,
        applyRulesInRealiteArchivingRules: true,
        newRule: {},
        processingPeriods: [],
        userEmails: ["aleksandari@realtimetraffic.com.au"]
      }
      this.runConfirmActive = true
    },
    onRunSiteRules(site) {
      this.runConfirmLevel = 'site'
      this.runConfirmEntityName = site ? site.siteName : ''
      this.runConfirmAppliedRule = this.appliedSiteRuleGetter(site.siteId)
      this.runConfirmPayload = {
        triggerBy: "site",
        id: site.siteId,
        applyRulesInRealiteArchivingRules: true,
        newRule: {},
        processingPeriods: [],
        userEmails: ["aleksandari@realtimetraffic.com.au"]
      }
      this.runConfirmActive = true
    },
    onRunDeviceRules(device, site) {
      this.runConfirmLevel = 'device'
      this.runConfirmEntityName = device ? device.name : ''
      this.runConfirmAppliedRule = this.appliedDeviceRuleGetter(device.dataDeviceId)
      this.runConfirmPayload = {
        triggerBy: "dataDevice",
        id: device.dataDeviceId,
        applyRulesInRealiteArchivingRules: true,
        newRule: {},
        processingPeriods: [],
        userEmails: ["aleksandari@realtimetraffic.com.au"]
      }
      this.runConfirmActive = true
    },
    async executeRun() {
      this.runConfirmActive = false
      this.isLoading = true
      const response = await this.triggerArchiving(this.runConfirmPayload)
      if (response instanceof Error) {
        this.showToast(`Error: ${response.message}`, 'is-danger')
      } else if (response.type === 'success') {
        this.showToast('Archiving triggered successfully', 'is-success')
        await this.refreshData()
      }
      this.isLoading = false
    },
    onRestoreProject() {
      this.restoreConfirmLevel = 'project'
      this.restoreConfirmEntityName = this.selectedArchiveProject ? this.selectedArchiveProject.name : ''
      this.restoreConfirmPayload = {
        triggerBy: "project",
        id: this.selectedArchiveProject._id,
        processingPeriods: [],
        userEmails: ["aleksandari@realtimetraffic.com.au"]
      }
      this.restoreConfirmActive = true
    },
    onRestoreSite(site) {
      this.restoreConfirmLevel = 'site'
      this.restoreConfirmEntityName = site ? site.siteName : ''
      this.restoreConfirmPayload = {
        triggerBy: "site",
        id: site.siteId,
        processingPeriods: [],
        userEmails: ["aleksandari@realtimetraffic.com.au"]
      }
      this.restoreConfirmActive = true
    },
    onRestoreDevice(device) {
      this.restoreConfirmLevel = 'device'
      this.restoreConfirmEntityName = device ? device.name : ''
      this.restoreConfirmPayload = {
        triggerBy: "dataDevice",
        id: device.dataDeviceId,
        processingPeriods: [],
        userEmails: ["aleksandari@realtimetraffic.com.au"]
      }
      this.restoreConfirmActive = true
    },
    async executeRestore() {
      this.restoreConfirmActive = false
      this.isLoading = true
      const response = await this.triggerRestoring(this.restoreConfirmPayload)
      if (response instanceof Error) {
        this.showToast(`Error: ${response.message}`, 'is-danger')
      } else if (response.type === 'success') {
        this.showToast('Restoring triggered successfully', 'is-success')
        await this.refreshData()
      }
      this.isLoading = false
    },
    onCloneClicked(level, data) {
      if (level === 'project') {
        this.cloneDialogLevel = 'project'
        this.cloneDialogEntityName = this.selectedArchiveProject ? this.selectedArchiveProject.name : ''
        this.cloneDialogEntityId = this.selectedArchiveProject ? this.selectedArchiveProject._id : ''
      } else if (level === 'site') {
        this.cloneDialogLevel = 'site'
        this.cloneDialogEntityName = data ? data.siteName : ''
        this.cloneDialogEntityId = data ? data.siteId : ''
      } else if (level === 'device') {
        this.cloneDialogLevel = 'dataDevice'
        this.cloneDialogEntityName = data ? data.name : ''
        this.cloneDialogEntityId = data ? data.dataDeviceId : ''
      }
      this.cloneDialogActive = true
    },
    async onClone(payload) {
      console.log('Clone payload:', payload)
      this.cloneDialogActive = false
      this.isLoading = true
      const response = await this.triggerCloning(payload)
      if (response instanceof Error) {
        this.showToast(`Error: ${response.message}`, 'is-danger')
      } else if (response.type === 'success') {
        this.showToast('Cloning triggered successfully', 'is-success')
      }
      this.isLoading = false
    },
    capitalizeFirst(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    getStatusCardClass(key) {
      const classMap = {
        active: 'status-card-active',
        archived: 'status-card-archived',
        deleted: 'status-card-deleted',
        restoring: 'status-card-restoring',
      }
      return classMap[key] || ''
    },
    findRule(level, id) {
      if (!this.allRules || !id) return null
      if (level === 'site') {
        return this.allRules.find(r => r.siteId === id && r.ruleAppliedAtLevel === 'site' && r.currentStatus === 'active') || null
      }
      if (level === 'device') {
        return this.allRules.find(r => r.dataDeviceId === id && r.ruleAppliedAtLevel === 'dataDevice' && r.currentStatus === 'active') || null
      }
      return null
    },
    showToast(message, type) {
      this.$buefy.toast.open({
        message: message,
        duration: 5000,
        type: type
      })
    },
  },
  computed: {
    ...mapGetters({
      selectedArchiveProject: 'archiveModule/getSelectedArchiveProject',
      projectData: 'archiveModule/getStatusDataBySite',
      allRules: 'archiveModule/getAllRules',
      appliedProjectRuleGetter: 'archiveModule/getAppliedProjectRule',
      appliedSiteRuleGetter: 'archiveModule/getAppliedSiteRule',
      appliedDeviceRuleGetter: 'archiveModule/getAppliedDeviceRule',
    }),
    currentDestinationOptions() {
      const stage = process.env.VUE_APP_STAGE
      return this.allowedDestinationOptions[stage] || []
    },
    projectRule() {
      if (!this.allRules || !this.selectedArchiveProject) return null
      return this.allRules.find(r => r.projectId === this.selectedArchiveProject._id && r.ruleAppliedAtLevel === 'project' && r.currentStatus === 'active') || null
    },
  }
}
</script>

<style scoped lang="scss">

.shadow {
  box-shadow: 0 0.125em 0.125em -0.125em rgba(10, 10, 10, 0.1), 0 0px 0 1px rgba(10, 10, 10, 0.02);
  background: white;
  border-radius: 8px;
}

.status-card {
  border-radius: 6px;
  min-width: 140px;
  flex: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border-left: 6px solid transparent;
}

.status-card-active {
  border-left-color: #23d160;
}

.status-card-archived {
  border-left-color: #209cee;
}

.status-card-deleted {
  border-left-color: #ff3860;
}

.status-card-restoring {
  border-left-color: #ffdd57;
}

.applied-rule-box {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
}

.count-indicator {
  border-radius: 4px;
  padding: 3px 0;
  font-size: 12px;
  min-width: 100px;
  text-align: center;
}

</style>